import { useCallback } from 'react'

import { Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { useFormik } from 'formik'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { BasicInfoStep } from './steps/BasicInfoStep'
import { EditorsStep } from './steps/EditorsStep'
import { ParticipantsStep } from './steps/ParticipantsStep'
import { PublishStep } from './steps/PublishStep'
import type { SurveyFormData } from './types'

const DEFAULT_VALUES: SurveyFormData = {
  title: '',
  description: '',
  anonymous: false,
  restrictedViewersEnabled: false,
  editorSelection: {
    type: 'manual',
    ids: [],
  },
  respondentStrategy: {
    strategy: 'all_employees',
    arguments: [],
  },
  endsAt: '',
}

interface Props {
  title: string
  initialValues?: SurveyFormData
  onClose: () => void
  onSubmit: (data: SurveyFormData) => void
  surveyId?: number
  action: 'edit' | 'publish'
  publishValidationErrors?: string[]
}

export function SurveyWizard({
  title,
  initialValues,
  onClose,
  onSubmit,
  surveyId,
  action,
  publishValidationErrors = [],
}: Props) {
  const { employee } = useCurrentSession()

  const form = useFormik<SurveyFormData>({
    initialValues: initialValues || DEFAULT_VALUES,
    onSubmit: (values) => {
      // Ensure current user is included as editor
      const currentUserIncluded =
        values.editorSelection.type === 'manual' && values.editorSelection.ids.includes(employee.id)

      const editorSelection = currentUserIncluded
        ? values.editorSelection
        : values.editorSelection.type === 'manual'
          ? { type: 'manual' as const, ids: [...values.editorSelection.ids, employee.id] }
          : values.editorSelection

      onSubmit({
        ...values,
        editorSelection,
      })
    },
  })

  const formHandleSubmit = form.handleSubmit
  const handleSubmit = useCallback(() => {
    if (form.isSubmitting) return
    formHandleSubmit()
  }, [formHandleSubmit, form.isSubmitting])

  const baseSteps = [
    {
      title: i18n.t('surveys.create_survey_wizard.steps.basic_info'),
      nextText: i18n.t('common.next'),
      render: (props: WizardStepRenderParams<SurveyFormData>) => (
        <BasicInfoStep {...props} errors={form.errors} />
      ),
      isCompleted: () => Boolean(form.values.title),
    },
    {
      title: i18n.t('surveys.create_survey_wizard.steps.editors_and_visibility'),
      nextText: i18n.t('common.next'),
      render: (props: WizardStepRenderParams<SurveyFormData>) => (
        <EditorsStep {...props} errors={form.errors} />
      ),
      isCompleted: () => true,
    },
    {
      title: i18n.t('surveys.create_survey_wizard.steps.participants_and_privacy'),
      nextText: action === 'publish' ? i18n.t('common.next') : i18n.t('common.submit'),
      render: (props: WizardStepRenderParams<SurveyFormData>) => (
        <ParticipantsStep {...props} errors={form.errors} />
      ),
      isCompleted: () => true,
    },
  ]

  const publishStep =
    action === 'publish' && surveyId
      ? [
          {
            title: i18n.t('surveys.publish_dialog.title'),
            nextText: i18n.t('surveys.publish_dialog.publish'),
            render: (props: WizardStepRenderParams<SurveyFormData>) => (
              <PublishStep {...props} errors={form.errors} surveyId={surveyId} />
            ),
            isCompleted: () => publishValidationErrors.length === 0,
          },
        ]
      : []

  const hasBasicInfo =
    !!form.values.title &&
    form.values.title !== i18n.t('surveys.default_title') &&
    !!form.values.description
  const defaultStepIndex = action === 'publish' && hasBasicInfo ? 1 : 0

  return (
    <Wizard.Modal
      size="large"
      title={title}
      icon="document-folder-board-edit"
      initialData={form.values}
      defaultStepIndex={defaultStepIndex}
      onSubmit={handleSubmit}
      onClose={onClose}
      onDataChanged={(newData) => form.setValues(newData)}
      steps={[...baseSteps, ...publishStep]}
    />
  )
}
